import React, { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";

import {
  Article,
  Dashboard,
  Launch,
  Lock,
  Settings,
} from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { Box, SxProps } from "@mui/system";

import { ButtonLink } from "decodable-ui/lib/components/Link";
import ConnectionIcon from "decodable-ui/lib/components/icons/Connection";
import PipelineIcon from "decodable-ui/lib/components/icons/Pipeline";
import StreamIcon from "decodable-ui/lib/components/icons/Stream";

import { GroupIcon } from "../access/components/groups/GroupIcon";

import { DecodableLogoSmall } from "./DecodableLogoSmall";

const iconStyle = { fontSize: "1.4rem" };

const navItemPadding = "1ch";

export function SideNav({ sx }: { sx: SxProps }) {
  const { accountName } = useParams();

  const upperNav = useMemo(
    () => [
      <NavItem
        icon={<Dashboard sx={{ color: "primary", ...iconStyle }} />}
        label="Dashboard"
        key="Dashboard"
        href={`/${accountName}`}
      />,
      <NavItem
        icon={<ConnectionIcon style={iconStyle} />}
        label="Connections"
        key="Connections"
        href="connections"
      />,
      <NavItem
        icon={<StreamIcon style={iconStyle} />}
        label="Streams"
        key="Streams"
        href="streams"
      />,
      <NavItem
        icon={<PipelineIcon style={iconStyle} />}
        label="Pipelines"
        key="Pipelines"
        href="pipelines"
      />,
    ],
    []
  );

  const lowerNav = useMemo(
    () => [
      <NavItem
        icon={<Lock style={iconStyle} />}
        label="Secrets"
        key="Secrets"
        href="secrets"
      />,
      <NavItem
        icon={<GroupIcon style={iconStyle} />}
        label="Access Control"
        key="Access Control"
        stem="access"
        href="access/users"
      />,
      <NavItem
        icon={<Settings style={iconStyle} />}
        label="Account Management"
        key="Account Management"
        href="account"
      />,
      <NavItem
        label={
          <span>
            Docs{" "}
            <Launch
              sx={{
                fontSize: "0.9em",
                verticalAlign: "middle",
              }}
            />
          </span>
        }
        key="Docs"
        icon={<Article style={iconStyle} />}
        href="https://docs.decodable.co/"
      />,
    ],
    []
  );

  return (
    <Box
      sx={{
        maxHeight: "100%",
        width: "10ch",
        bgcolor: "background.paper",
        borderRight: `solid 1px ${gray(90)}`,

        display: "grid",
        gridTemplateRows: "1fr auto",
        placeItems: "stretch",

        ...sx,
      }}
    >
      <Box
        component="nav"
        sx={{
          display: "grid",
          placeItems: "stretch",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "stretch",
            gridTemplateRows: `auto repeat(${upperNav.length}, auto) 1fr repeat(${lowerNav.length}, auto)`,
            gridTemplateColumns: "1fr",
          }}
        >
          <ButtonLink
            href={`/${accountName}`}
            disableRipple
            sx={{ "&:hover": { bgcolor: "initial" } }}
          >
            <DecodableLogoSmall
              viewBox="0 0 100 100"
              style={{
                placeSelf: "center",
                width: "36px",
                height: "36px",
                margin: `calc(1.3rem + ${navItemPadding}) 0 1.3rem`,
              }}
            />
          </ButtonLink>
          {upperNav}
          <Box />
          {lowerNav}
        </Box>
      </Box>
      <Box
        sx={{
          borderTop: "1px solid rgb(90% 90% 90%)",
          height: "85px",
          width: "100%",
          margin: 0,
          padding: 0,
        }}
      />
    </Box>
  );
}

const NavItem = function NavItem({
  icon,
  label,
  href,
  stem = href,
  onClick,
  sx,
}: {
  icon: JSX.Element;
  label: React.ReactNode;
  href?: string;
  stem?: string;
  onClick?: () => void;
  sx?: SxProps;
}) {
  const location = useLocation();
  const pathname = location.pathname;
  const stemLocation = pathname[0] == "/" ? pathname.indexOf("/", 1) + 1 : 0;

  const isActive =
    location.pathname === href || (stem && stemLocation > 0)
      ? location.pathname.slice(stemLocation, stemLocation + stem.length) ==
        stem
      : false;

  const isExternalLink = href?.startsWith("http");
  const target = isExternalLink ? "_blank" : undefined;
  const ButtonComponent = isExternalLink ? Button : ButtonLink;

  return (
    <ButtonComponent
      href={href}
      target={target}
      onClick={onClick}
      sx={{
        width: "100%",
        overflow: "hidden",

        padding: navItemPadding,
        margin: 0,

        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "auto auto",
        placeItems: "center",

        fontWeight: 600,
        borderRadius: 0,
        color: isActive ? "text.primary" : "text.secondary",
        "&:hover": {
          color: "text.primary",
        },
        boxSizing: "border-box",
        borderRight: isActive ? "solid 2px black" : "none",
        borderLeft: "none",
        borderTop: "none",
      }}
    >
      <Box>{icon}</Box>
      <Box
        sx={{
          fontSize: "0.7rem",
          paddingX: "0.2ch",
          textAlign: "center",
          lineHeight: "0.8rem",
        }}
      >
        {label}
      </Box>
    </ButtonComponent>
  );
};

function gray(percent: number) {
  return `rgb(${percent}% ${percent}% ${percent}%)`;
}

const communitySlackUrl =
  "https://decodablecommunity.slack.com/join/shared_invite/zt-uvow71bk-Uf914umgpoyIbOQSxriJkA#/shared-invite/email";
